import { copyToClipboard } from '@ctw/shared/components/copy-to-clipboard';
import { ContentError } from '@ctw/shared/components/errors/content-error';
import { Checkbox } from '@ctw/shared/components/form/checkbox';
import { Heading } from '@ctw/shared/components/heading';
import { notify } from '@ctw/shared/components/toast';
import { useCdaSectionSelectionContext } from '@ctw/shared/content/document-viewer/_internal/renderers/context/cda-section-selection-provider';
import { useDocumentViewerContext } from '@ctw/shared/content/document-viewer/_internal/renderers/context/document-viewer-provider';
import { ReactHtmlRenderer } from '@ctw/shared/content/document-viewer/_internal/renderers/react/react-html-renderer';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useZuiContext } from '@ctw/shared/context/zui-provider';
import {
  DOCUMENT_VIEWER_SECTION_CLASSNAME,
  DOCUMENT_VIEWER_SECTION_HEADING_TEST_ID,
  DOCUMENT_VIEWER_SECTION_TEST_ID,
} from '@ctw/shared/test/ids';
import type { CdaSection } from '@ctw/shared/utils/cda/types';
import { uniqueIdentifierHash } from '@ctw/shared/utils/hash';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { pathWithParameters } from '@ctw/shared/utils/url';
import { faFileCircleXmark, faFileHalfDashed, faLink } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { camelCase } from 'lodash-es';
import { memo } from 'react';

interface ReactSectionRendererProps {
  cdaSection: CdaSection;
}

export const ReactSectionRenderer = memo(
  ({ cdaSection }: ReactSectionRendererProps) => {
    const telemetry = useTelemetry();
    const { navigate } = useZuiContext();
    const { controls } = useDocumentViewerContext();
    const { selectedSectionIds, selectSectionId, deselectSectionId } =
      useCdaSectionSelectionContext();
    const cdaSectionHash = uniqueIdentifierHash(cdaSection.parsedHtmlNodes);

    return cdaSection.id && cdaSection.title ? (
      <section
        id={cdaSection.domId}
        data-testid={DOCUMENT_VIEWER_SECTION_TEST_ID}
        className={twx('flex flex-col gap-4', DOCUMENT_VIEWER_SECTION_CLASSNAME)}
      >
        <Heading level="h2" className={tw`group m-0 flex w-fit max-w-fit items-center gap-2 p-0`}>
          <Checkbox
            checked={selectedSectionIds.includes(cdaSection.id)}
            onChange={(checked) => {
              if (checked) {
                telemetry.trackInteraction('document_viewer_section_select', {
                  sectionIndex: cdaSection.index,
                });
                selectSectionId(cdaSection.id);
              } else {
                telemetry.trackInteraction('document_viewer_section_deselect', {
                  sectionIndex: cdaSection.index,
                });
                deselectSectionId(cdaSection.id);
              }
            }}
            name={`selectSection_${camelCase(cdaSection.id)}`}
            label={`Select section '${cdaSection.title}'`}
            labelVisuallyHidden={true}
            className={tw`mt-4`}
          />

          <a
            role={controls.deepLinkSections ? 'link' : 'generic'}
            href={`#${cdaSection.domId}`}
            className={twx('flex h-full w-full items-center gap-2', {
              'cursor-default': !controls.deepLinkSections,
            })}
            data-testid={DOCUMENT_VIEWER_SECTION_HEADING_TEST_ID}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();

              if (controls.deepLinkSections) {
                telemetry.trackInteraction('document_viewer_section_heading_click', {
                  sectionTitle: cdaSection.title,
                });

                const sectionDeepLinkPath = pathWithParameters(
                  `${window.location.pathname}${window.location.search}`,
                  {
                    documentSectionId: cdaSection.id,
                  },
                  ['builderId'],
                );

                void copyToClipboard(
                  `${window.location.protocol}//${window.location.host}${sectionDeepLinkPath}`,
                ).then(() => {
                  navigate(sectionDeepLinkPath);
                  notify({
                    type: 'success',
                    title: 'Copied Section Link',
                    body: 'A shareable link to this document section has been copied to your clipboard.',
                  });
                });
              }
            }}
          >
            <span
              id={`${cdaSection.domId}-title`}
              data-search-index={cdaSection.titleSearchIndex}
              className={tw`pt-4 font-medium text-xl leading-7`}
            >
              {cdaSection.title}
            </span>
            {controls.deepLinkSections && (
              <FontAwesomeIcon
                className={tw`h-5 w-5 pt-4 text-content-subtle opacity-0 transition-all duration-300 group-hover:opacity-50`}
                icon={faLink}
              />
            )}
          </a>
        </Heading>
        <div className={tw`w-full max-w-full flex-1 overflow-hidden`}>
          {cdaSection.parseError ? (
            <ContentError
              icon={faFileCircleXmark}
              message="This section could not be displayed due to invalid document XML."
              className={tw`mx-auto flex h-full min-h-full w-[300px] min-w-sm max-w-full items-center justify-center`}
            />
          ) : (
            <ReactHtmlRenderer
              id={`${cdaSection.id}-html|${cdaSectionHash}`}
              key={`${cdaSection.id}-html|${cdaSectionHash}`}
              parsedHtmlNodes={cdaSection.parsedHtmlNodes ?? []}
              emptyState={
                <ContentError
                  icon={faFileHalfDashed}
                  message="There is no content available for this section of the document."
                  className={tw`mx-auto flex h-full min-h-full w-[300px] min-w-sm max-w-full items-center justify-center`}
                />
              }
            />
          )}
        </div>
      </section>
    ) : (
      <></>
    );
  },
  (prevProps, nextProps) => prevProps.cdaSection.id === nextProps.cdaSection.id,
);
